import React, { FC, HTMLAttributes } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, TextField, FormHelperText, Typography } from '@mui/material';
import {
  formatCpfCnpj,
  removeCaracters,
} from '@cigam/template/dist/utils/formatCpfCnpj';
import { LoadingButton } from '@mui/lab';
import { useSettings } from '@cigam/template/dist/hooks/useSettings';
import { loginWithCpfCnpjAndEmail } from '../../../services/authService';
import { UserContatoDTO } from '../../../types/dtos/auth';

const PREFIX = 'LoginForm';

const classes = {
  root: `${PREFIX}-root`,
};

const StyledFormik = styled(Formik)(() => ({
  [`& .${classes.root}`]: {},
}));

interface Props extends HTMLAttributes<HTMLFormElement> {
  onSubmitGetIn: () => void;
  setUser: (user: UserContatoDTO) => void;
}

export const LoginForm: FC<Props> = ({
  className,
  onSubmitGetIn,
  setUser,
  ...rest
}) => {
  const { settings } = useSettings();
  const portalAtivo = settings.activePortals[0];

  return (
    <StyledFormik
      initialValues={{
        cpfCnpj: '',
        email: '',
      }}
      validationSchema={Yup.object().shape({
        cpfCnpj: Yup.string().max(255).required('CPF/CNPJ é obrigatório'),
        email: Yup.string()
          .email('E-mail inválido')
          .required('E-mail é obrigatório'),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          const user = await loginWithCpfCnpjAndEmail(
            portalAtivo,
            removeCaracters(values.cpfCnpj),
            values.email
          );

          setUser({
            ...user[0],
            CpfCnpj: removeCaracters(values.cpfCnpj),
            Email: values.email,
          });

          onSubmitGetIn();
        } catch (error) {
          let message;

          if (error instanceof Error) message = error.message;
          else message = String(error);

          if (!message) message = 'Conexão inválida com o servidor';

          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form
          noValidate
          className={clsx(classes.root, className)}
          onSubmit={handleSubmit}
          {...rest}
        >
          <Typography variant="h4" color="textPrimary">
            Faça login
          </Typography>
          <TextField
            error={Boolean(touched.cpfCnpj && errors.cpfCnpj)}
            fullWidth
            autoFocus
            helperText={touched.cpfCnpj && errors.cpfCnpj}
            label="CPF/CNPJ"
            margin="normal"
            name="cpfCnpj"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={formatCpfCnpj(values.cpfCnpj)}
          />
          <TextField
            error={Boolean(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            label="E-mail"
            margin="normal"
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={values.email}
          />
          <Box mt={2}>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              Entrar
            </LoadingButton>
            {errors.submit && (
              <Box mt={3}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}
          </Box>
        </form>
      )}
    </StyledFormik>
  );
};
